import axios from 'axios';

const STORAGE_KEY = 'dropzone_games';

function getStorage() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage;
}

export async function triggerAnalysis({
  gcsUri,
  gameName,
  homeTeam,
  awayTeam,
  gameDate,
}) {
  console.log('[DropZone] triggerAnalysis request', {
    gcsUri,
    gameName,
    homeTeam,
    awayTeam,
    gameDate,
  });

  const response = await axios.post('/api/video-analysis/trigger', {
    gcsUri,
    gameName,
    homeTeam,
    awayTeam,
    gameDate,
  });

  console.log('[DropZone] triggerAnalysis response', response.data);

  return {
    jobId: response.data.jobId,
    status: response.data.status,
  };
}

export async function getJobStatus(jobId) {
  console.log('[DropZone] getJobStatus request', { jobId });
  const response = await axios.get(`/api/video-analysis/status/${jobId}`);
  console.log('[DropZone] getJobStatus response', response.data);
  return response.data;
}

export function subscribeToProgress(jobId, onEvent) {
  console.log('[DropZone] subscribeToProgress open', { jobId });

  const source = new EventSource(`/api/video-analysis/progress/${jobId}`);

  source.onmessage = (event) => {
    try {
      const parsed = JSON.parse(event.data);
      console.log('[DropZone] subscribeToProgress event', parsed);
      onEvent(parsed);
    } catch (error) {
      console.error('[DropZone] subscribeToProgress parse error', error);
    }
  };

  source.onerror = (error) => {
    console.error('[DropZone] subscribeToProgress error', { jobId, error });
  };

  return {
    close() {
      console.log('[DropZone] subscribeToProgress close', { jobId });
      source.close();
    },
  };
}

// ─── Direct-to-GCS upload via resumable signed URLs ───
// Uploads go Browser → GCS directly, bypassing server body size limits.
// Supports files up to 5GB. Progress tracking via XMLHttpRequest.

const DIRECT_UPLOAD_THRESHOLD = 50 * 1024 * 1024; // 50MB — use direct GCS for files above this

function uploadFileDirectToGcs(resumableUrl, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', resumableUrl, true);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && typeof onProgress === 'function') {
        onProgress({ loaded: event.loaded, total: event.total });
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`GCS upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during GCS upload'));
    xhr.ontimeout = () => reject(new Error('GCS upload timed out'));
    xhr.timeout = 0; // No timeout for large uploads

    xhr.send(file);
  });
}

export async function uploadVideoToGcs(file, onProgress) {
  console.log('[DropZone] uploadVideoToGcs request', {
    name: file?.name,
    size: file?.size,
    type: file?.type,
  });

  // Use direct GCS upload for large files
  if (file.size > DIRECT_UPLOAD_THRESHOLD) {
    return uploadFilesToGcs([file], onProgress);
  }

  const formData = new FormData();
  formData.append('files', file);

  const response = await axios.post('/api/drop-zone/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (event) => {
      if (!event.total) return;
      const progress = Math.round((event.loaded / event.total) * 100);
      console.log('[DropZone] uploadVideoToGcs progress', { loaded: event.loaded, total: event.total, progress });
      if (typeof onProgress === 'function') onProgress(progress);
    },
  });

  console.log('[DropZone] uploadVideoToGcs response', response.data);
  return {
    gcsUri: response.data.gcsUri,
    filename: response.data.filename,
    sessionId: response.data.sessionId,
    files: response.data.files,
    videos: response.data.videos,
    xmlFiles: response.data.xmlFiles,
    zipFiles: response.data.zipFiles,
  };
}

export async function uploadFilesToGcs(files, onProgress) {
  const totalSize = files?.reduce((sum, f) => sum + (f.size || 0), 0) || 0;
  const anyLargeFile = files?.some((f) => f.size > DIRECT_UPLOAD_THRESHOLD);

  console.log('[DropZone] uploadFilesToGcs request', {
    count: files?.length,
    totalSize,
    names: files?.map((f) => f.name),
    directUpload: anyLargeFile,
  });

  // ─── Direct-to-GCS path (for large files) ───
  if (anyLargeFile) {
    console.log('[DropZone] DIRECT-TO-GCS path selected (file > 50MB threshold)', {
      threshold: `${DIRECT_UPLOAD_THRESHOLD / 1024 / 1024}MB`,
      largestFile: Math.max(...files.map(f => f.size)),
    });

    // Step 1: Get resumable upload URLs from backend
    console.log('[DropZone] Step 1: Requesting resumable upload URIs from /api/drop-zone/init-upload');
    const initResponse = await axios.post('/api/drop-zone/init-upload', {
      files: files.map((f) => ({ name: f.name, size: f.size, type: f.type })),
    });

    console.log('[DropZone] Step 1 RESPONSE:', {
      status: initResponse.status,
      sessionId: initResponse.data.sessionId,
      uploadCount: initResponse.data.uploads?.length,
      uploads: initResponse.data.uploads?.map(u => ({
        name: u.originalName,
        gcsPath: u.gcsPath,
        hasResumableUrl: !!u.resumableUrl,
      })),
    });

    const { sessionId, uploads } = initResponse.data;
    let totalLoaded = 0;

    // Step 2: Upload each file directly to GCS
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const uploadInfo = uploads[i];

      console.log('[DropZone] direct GCS upload start', {
        name: file.name,
        size: file.size,
        gcsPath: uploadInfo.gcsPath,
      });

      await uploadFileDirectToGcs(uploadInfo.resumableUrl, file, ({ loaded, total }) => {
        const fileLoaded = totalLoaded + loaded;
        const progress = Math.round((fileLoaded / totalSize) * 100);
        console.log('[DropZone] uploadFilesToGcs progress', { loaded: fileLoaded, total: totalSize, progress });
        if (typeof onProgress === 'function') onProgress(progress);
      });

      totalLoaded += file.size;
      console.log('[DropZone] direct GCS upload done', { name: file.name });
    }

    // Step 3: Notify backend that uploads are complete
    const completeResponse = await axios.post('/api/drop-zone/complete-upload', {
      sessionId,
      files: uploads.map((u) => ({
        gcsPath: u.gcsPath,
        filename: u.filename,
        originalName: u.originalName,
        mimetype: u.mimetype,
      })),
    });

    console.log('[DropZone] uploadFilesToGcs response (direct)', completeResponse.data);
    return {
      sessionId: completeResponse.data.sessionId,
      gcsUri: completeResponse.data.gcsUri,
      filename: completeResponse.data.filename,
      files: completeResponse.data.files || [],
      videos: completeResponse.data.videos || [],
      xmlFiles: completeResponse.data.xmlFiles || [],
      zipFiles: completeResponse.data.zipFiles || [],
      size: completeResponse.data.size,
      errors: completeResponse.data.errors,
    };
  }

  // ─── Server-proxied path (for small files, <50MB) ───
  const formData = new FormData();
  for (const file of files) {
    formData.append('files', file);
  }

  const response = await axios.post('/api/drop-zone/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000,
    onUploadProgress: (event) => {
      if (!event.total) return;
      const progress = Math.round((event.loaded / event.total) * 100);
      console.log('[DropZone] uploadFilesToGcs progress', { loaded: event.loaded, total: event.total, progress });
      if (typeof onProgress === 'function') onProgress(progress);
    },
  });

  console.log('[DropZone] uploadFilesToGcs response', response.data);
  return {
    sessionId: response.data.sessionId,
    gcsUri: response.data.gcsUri,
    filename: response.data.filename,
    files: response.data.files || [],
    videos: response.data.videos || [],
    xmlFiles: response.data.xmlFiles || [],
    zipFiles: response.data.zipFiles || [],
    size: response.data.size,
    errors: response.data.errors,
  };
}

export function getGameHistory() {
  const storage = getStorage();
  if (!storage) {
    console.log('[DropZone] getGameHistory skipped (no localStorage)');
    return [];
  }

  try {
    const raw = storage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const history = Array.isArray(parsed) ? parsed : [];
    console.log('[DropZone] getGameHistory result', { count: history.length });
    return history;
  } catch (error) {
    console.error('[DropZone] getGameHistory error', error);
    return [];
  }
}

export function saveGameToHistory(game) {
  const storage = getStorage();
  if (!storage || !game) {
    console.log('[DropZone] saveGameToHistory skipped', {
      hasStorage: Boolean(storage),
      hasGame: Boolean(game),
    });
    return [];
  }

  const gameId = game.jobId || game.id;
  const existing = getGameHistory();
  const filtered = existing.filter((entry) => {
    const entryId = entry.jobId || entry.id;
    return entryId !== gameId;
  });

  const nextHistory = [{ ...game, jobId: gameId }, ...filtered].slice(0, 50);
  storage.setItem(STORAGE_KEY, JSON.stringify(nextHistory));
  console.log('[DropZone] saveGameToHistory result', {
    gameId,
    count: nextHistory.length,
  });

  return nextHistory;
}
